import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Users,
  UserPlus,
  Search,
  ChevronLeft,
  Stethoscope,
  Briefcase,
  Clock,
  Star,
  Phone,
  Mail,
  Calendar,
  Edit,
  Trash2
} from 'lucide-react';
import { format } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { staffApi, getLocalizedValue } from '@/services/clinicApi';

const ROLE_COLORS = {
  doctor: 'bg-blue-100 text-blue-700',
  nurse: 'bg-green-100 text-green-700',
  receptionist: 'bg-purple-100 text-purple-700',
  admin: 'bg-red-100 text-red-700',
  assistant: 'bg-amber-100 text-amber-700',
};

const ClinicStaff = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const lang = i18n.language;
  const locale = lang === 'ar' ? ar : enUS;

  const [staff, setStaff] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchStaff();
  }, [isAuthenticated, navigate]);

  const fetchStaff = async () => {
    setLoading(true);
    try {
      const [employeesRes, doctorsRes] = await Promise.all([
        staffApi.getEmployees().catch(() => null),
        staffApi.getDoctors().catch(() => null),
      ]);

      if (employeesRes?.data?.data) {
        setStaff(employeesRes.data.data);
      }
      if (doctorsRes?.data?.data) {
        setDoctors(doctorsRes.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch staff:', error);
      toast.error(lang === 'ar' ? 'فشل في تحميل الموظفين' : 'Failed to load staff');
    } finally {
      setLoading(false);
    }
  };

  const allStaff = [
    ...doctors.map(d => ({ ...d, role: 'doctor' })),
    ...staff,
  ];

  const filteredStaff = allStaff.filter((member) => {
    const name = getLocalizedValue(member.name, lang) || 
      `${getLocalizedValue(member.first_name, lang)} ${getLocalizedValue(member.last_name, lang)}`.trim();
    const matchesSearch = name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTab = activeTab === 'all' || member.role === activeTab || 
      (activeTab === 'doctors' && member.role === 'doctor');
    return matchesSearch && matchesTab;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          <Skeleton className="h-16 w-full" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} className="h-48" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="clinic-staff">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b dark:border-slate-700 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                  {lang === 'ar' ? 'إدارة الموظفين' : 'Staff Management'}
                </h1>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {allStaff.length} {lang === 'ar' ? 'موظف' : 'staff members'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative flex-1 md:w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder={lang === 'ar' ? 'بحث...' : 'Search...'}
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Button className="bg-teal-600 hover:bg-teal-700">
                <UserPlus className="h-4 w-4 mr-2" />
                {lang === 'ar' ? 'إضافة' : 'Add'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList>
            <TabsTrigger value="all">{lang === 'ar' ? 'الكل' : 'All'}</TabsTrigger>
            <TabsTrigger value="doctors">{lang === 'ar' ? 'الأطباء' : 'Doctors'}</TabsTrigger>
            <TabsTrigger value="nurse">{lang === 'ar' ? 'الممرضين' : 'Nurses'}</TabsTrigger>
            <TabsTrigger value="receptionist">{lang === 'ar' ? 'الاستقبال' : 'Receptionists'}</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Staff Grid */}
        {filteredStaff.length === 0 ? (
          <Card className="border-0 shadow-md">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Users className="h-16 w-16 text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                {lang === 'ar' ? 'لا يوجد موظفين' : 'No staff found'}
              </h3>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredStaff.map((member) => {
              const name = getLocalizedValue(member.name, lang) || 
                `${getLocalizedValue(member.first_name, lang)} ${getLocalizedValue(member.last_name, lang)}`.trim();
              const specialty = member.specialities?.[0]?.name 
                ? getLocalizedValue(member.specialities[0].name, lang)
                : null;
              
              return (
                <Card 
                  key={`${member.role}-${member.id}`} 
                  className="border-0 shadow-md hover:shadow-lg transition-shadow"
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <Avatar className="h-16 w-16">
                        <AvatarImage src={member.media?.[0]?.url || member.media?.[0]?.thumb} />
                        <AvatarFallback className="bg-teal-100 text-teal-700 text-lg">
                          {name.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-semibold text-slate-900 dark:text-white">
                              {name}
                            </h3>
                            {specialty && (
                              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                                {specialty}
                              </p>
                            )}
                          </div>
                          <Badge className={ROLE_COLORS[member.role] || 'bg-slate-100 text-slate-700'}>
                            {member.role === 'doctor' ? (lang === 'ar' ? 'طبيب' : 'Doctor') :
                             member.role === 'nurse' ? (lang === 'ar' ? 'ممرض' : 'Nurse') :
                             member.role === 'receptionist' ? (lang === 'ar' ? 'استقبال' : 'Reception') :
                             member.role}
                          </Badge>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 space-y-2">
                      {member.phone_number && (
                        <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                          <Phone className="h-4 w-4 text-slate-400" />
                          <span>{member.phone_number}</span>
                        </div>
                      )}
                      {member.email && (
                        <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                          <Mail className="h-4 w-4 text-slate-400" />
                          <span className="truncate">{member.email}</span>
                        </div>
                      )}
                    </div>

                    {member.role === 'doctor' && (
                      <div className="mt-4 pt-4 border-t dark:border-slate-700 flex items-center justify-between">
                        <div className="flex items-center gap-1">
                          <Star className="h-4 w-4 text-amber-400 fill-amber-400" />
                          <span className="text-sm font-medium">{member.rate || 0}</span>
                          <span className="text-sm text-slate-400">({member.total_reviews || 0})</span>
                        </div>
                        <div className="text-sm text-teal-600 font-medium">
                          {member.price || 0} {lang === 'ar' ? 'ل.س' : 'SYP'}
                        </div>
                      </div>
                    )}

                    <div className="mt-4 flex gap-2">
                      <Button variant="outline" size="sm" className="flex-1">
                        <Edit className="h-4 w-4 mr-1" />
                        {lang === 'ar' ? 'تعديل' : 'Edit'}
                      </Button>
                      <Button variant="outline" size="sm" className="flex-1">
                        <Calendar className="h-4 w-4 mr-1" />
                        {lang === 'ar' ? 'الجدول' : 'Schedule'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClinicStaff;
